import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Loader2, RefreshCw, ExternalLink, Trash2, Bell, BellOff, Info, TrendingUp, TrendingDown, Sparkles, CheckSquare, Square } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

// Keyword Analizi: resmi Etsy API'den ucretsiz talep/rekabet/firsat puani.
// Backend: apps/api/src/routes/research.ts + services/keyword-research.service.ts

interface KwListing {
  listingId: string; rank: number; title: string; url: string; imageUrl: string | null;
  price: string | null; priceUsd: number | null; currency: string | null; tags: string[];
  favorites: number; views: number | null; ageDays: number; favPerDay: number; viewsPerDay: number | null;
  personalizable: boolean; favGainPerDay?: number | null; viewGainPerDay?: number | null;
}
interface KwMetrics {
  totalResults: number; sampled: number; medianViewsPerDay: number | null; topViewsPerDay?: number | null; medianFavPerDay: number;
  top10MedianFavPerDay: number; newcomerShare: number; medianAgeDays: number; personalizablePct: number; digitalPct: number;
  price: { min: number | null; p25: number | null; median: number | null; p75: number | null; max: number | null };
  topTags: Array<{ tag: string; count: number; avgFavPerDay: number }>;
  realFavGainPerDay: number | null; realViewGainPerDay: number | null; comparedHours: number | null;
  labels: { demand: string; competition: string; opportunity: string };
}
interface KwResult {
  keywordId: string; keyword: string; isTracked: boolean; capturedAt: string; cached: boolean;
  demandScore: number; competitionScore: number; opportunityScore: number; metrics: KwMetrics; listings: KwListing[];
}
interface KwRow {
  id: string; keyword: string; isTracked: boolean; lastScannedAt: string;
  demandScore: number | null; competitionScore: number | null; opportunityScore: number | null;
  totalResults: number | null; medianPrice: number | null; opportunityChange: number | null;
}
interface KwCandidate {
  keyword: string; sources: Array<"google" | "tag">; tagCount?: number;
  opportunityScore?: number | null; demandScore?: number | null; competitionScore?: number | null; scannedAt?: string | null;
}
interface KwBatchRow { keyword: string; opportunityScore?: number; demandScore?: number; competitionScore?: number; totalResults?: number; error?: string }
const MAX_BATCH = 15;
interface KwHistoryPoint { capturedAt: string; opportunityScore: number | null; demandScore: number | null; competitionScore: number | null }

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/research${path}`, { ...init, headers: authHeaders() });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error((data as { error?: string }).error || `İstek başarısız (${r.status})`);
  return data as T;
}

const fmt = (n: number | null | undefined, d = 0) => (n == null ? "—" : n.toLocaleString("tr-TR", { maximumFractionDigits: d }));
const usd = (n: number | null | undefined) => (n == null ? "—" : `$${n.toFixed(2)}`);

function scoreTone(score: number, inverse = false) {
  const s = inverse ? 100 - score : score;
  return s >= 55 ? "text-emerald-700 bg-emerald-50" : s >= 35 ? "text-amber-700 bg-amber-50" : "text-red-600 bg-red-50";
}

function ScoreCard({ title, score, label, inverse, hint }: { title: string; score: number; label: string; inverse?: boolean; hint: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm" title={hint}>
      <div className="text-xs font-medium text-gray-500">{title}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-3xl font-bold text-gray-900">{Math.round(score)}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${scoreTone(score, inverse)}`}>{label}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-gray-100">
        <div className={`h-1.5 rounded-full ${inverse ? "bg-gray-400" : "bg-indigo-600"}`} style={{ width: `${Math.max(2, score)}%` }} />
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

function Sparkline({ points }: { points: KwHistoryPoint[] }) {
  const ys = points.map((p) => p.opportunityScore ?? 0);
  if (ys.length < 2) return null;
  const w = 220, h = 40;
  const d = ys.map((y, i) => `${i ? "L" : "M"}${((i / (ys.length - 1)) * w).toFixed(1)},${(h - (y / 100) * h).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible" aria-label="Fırsat puanı geçmişi">
      <path d={d} fill="none" stroke="#4f46e5" strokeWidth={2} />
    </svg>
  );
}

export function KeywordResearchTab() {
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const [active, setActive] = useState<KwResult | null>(null);

  const listQ = useQuery({ queryKey: ["research-keywords"], queryFn: () => api<{ keywords: KwRow[] }>("/keywords") });
  const historyQ = useQuery({
    queryKey: ["research-history", active?.keywordId],
    queryFn: () => api<{ history: KwHistoryPoint[] }>(`/keywords/${active!.keywordId}/history`),
    enabled: !!active,
  });

  const scan = useMutation({
    mutationFn: ({ q, force }: { q: string; force?: boolean }) =>
      api<KwResult>(`/keyword?q=${encodeURIComponent(q)}${force ? "&force=1" : ""}`),
    onSuccess: (r) => {
      setActive(r);
      setInput(r.keyword);
      qc.invalidateQueries({ queryKey: ["research-keywords"] });
      qc.invalidateQueries({ queryKey: ["research-history", r.keywordId] });
    },
  });
  const track = useMutation({
    mutationFn: ({ id, tracked }: { id: string; tracked: boolean }) => api(`/keywords/${id}/track`, { method: "POST", body: JSON.stringify({ tracked }) }),
    onSuccess: (_d, v) => {
      if (active?.keywordId === v.id) setActive({ ...active, isTracked: v.tracked });
      qc.invalidateQueries({ queryKey: ["research-keywords"] });
    },
  });
  const [longTail, setLongTail] = useState<{ seed: string; candidates: KwCandidate[] } | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const expand = useMutation({
    mutationFn: (q: string) => api<{ seed: string; candidates: KwCandidate[] }>(`/expand?q=${encodeURIComponent(q)}`),
    onSuccess: (r) => {
      setLongTail(r);
      // Henuz puanlanmamis ilk adaylar onceden secili gelir
      setPicked(new Set(r.candidates.filter((c) => c.opportunityScore == null).slice(0, 10).map((c) => c.keyword)));
    },
  });
  const batch = useMutation({
    mutationFn: (keywords: string[]) => api<{ results: KwBatchRow[] }>("/batch", { method: "POST", body: JSON.stringify({ keywords }) }),
    onSuccess: (r) => {
      const byKw = new Map(r.results.map((x) => [x.keyword, x]));
      setLongTail((lt) => lt && {
        ...lt,
        candidates: lt.candidates.map((c) => {
          const x = byKw.get(c.keyword);
          return x && !x.error ? { ...c, opportunityScore: x.opportunityScore, demandScore: x.demandScore, competitionScore: x.competitionScore, scannedAt: new Date().toISOString() } : c;
        }),
      });
      setPicked(new Set());
      qc.invalidateQueries({ queryKey: ["research-keywords"] });
    },
  });
  const togglePick = (k: string) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(k)) n.delete(k); else if (n.size < MAX_BATCH) n.add(k);
    return n;
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/keywords/${id}`, { method: "DELETE" }),
    onSuccess: (_d, id) => {
      if (active?.keywordId === id) setActive(null);
      qc.invalidateQueries({ queryKey: ["research-keywords"] });
    },
  });

  const run = (q: string, force = false) => {
    const t = q.trim();
    if (t.length >= 2) scan.mutate({ q: t, force });
  };
  const m = active?.metrics;
  const rows = listQ.data?.keywords ?? [];

  return (
    <div className="space-y-4">
      {/* Arama */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); run(input); }}>
          <div className="relative min-w-[260px] flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={input} onChange={(e) => setInput(e.target.value)} placeholder="Anahtar kelime (İngilizce), örn. custom name necklace"
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
          <button type="submit" disabled={scan.isPending || input.trim().length < 2}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {scan.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Analiz et
          </button>
        </form>
        <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-500">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Etsy arama hacmi paylaşmaz. Talep, ilk sıralardaki ilanların günlük görüntülenme/favori hızından; rekabet, toplam ilan sayısından hesaplanır.
          Takibe alınan kelimeler her gün taranır, ikinci taramadan itibaren gerçek artış hızı da puana girer. Her analiz 2 Etsy API isteği kullanır; aynı kelime 20 saat önbellekte tutulur.
        </p>
        {scan.error && <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{(scan.error as Error).message}</div>}
      </div>

      {active && m && (
        <>
          {/* Baslik + eylemler */}
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">“{active.keyword}”</h2>
            <span className="text-xs text-gray-400">
              {new Date(active.capturedAt).toLocaleString("tr-TR")}{active.cached ? " · önbellekten" : ""}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => track.mutate({ id: active.keywordId, tracked: !active.isTracked })}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium ${active.isTracked ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`}>
                {active.isTracked ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                {active.isTracked ? "Günlük takipte" : "Takibe al"}
              </button>
              <button onClick={() => expand.mutate(active.keyword)} disabled={expand.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {expand.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Uzun kuyruk bul
              </button>
              <button onClick={() => run(active.keyword, true)} disabled={scan.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                <RefreshCw className="h-3.5 w-3.5" /> Yeniden tara
              </button>
            </div>
          </div>

          {/* Puanlar */}
          <div className="grid gap-3 sm:grid-cols-3">
            <ScoreCard title="Fırsat" score={active.opportunityScore} label={m.labels.opportunity} hint="Talep ağırlıklı; düşük rekabet ve yeni ilanların üst sıralara girebilmesi puanı artırır." />
            <ScoreCard title="Talep" score={active.demandScore} label={m.labels.demand} hint="İlk 24 ilanın günlük görüntülenme (yoksa favori) hızının medyanı. İkinci taramadan sonra gerçek artış hızı kullanılır." />
            <ScoreCard title="Rekabet" score={active.competitionScore} label={m.labels.competition} inverse hint="Toplam aktif ilan sayısı ve ilk 10 ilanın gücü. Yüksek = zor." />
          </div>

          {/* Olculer */}
          <div className="grid gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:grid-cols-4">
            <Stat label="Toplam ilan" value={fmt(m.totalResults)} sub={`${m.sampled} ilan incelendi`} />
            <Stat label="Görüntülenme / gün (en iyi %10)" value={fmt(m.topViewsPerDay ?? m.medianViewsPerDay, 1)} sub={m.topViewsPerDay != null ? "talebin dayanağı: 100 ilanın 10. en iyisi" : "eski tarama: ilk 24 ilanın medyanı"} />
            <Stat label="Favori / gün (medyan)" value={fmt(m.medianFavPerDay, 2)} sub={`ilk 10: ${fmt(m.top10MedianFavPerDay, 2)}`} />
            <Stat
              label="Gerçek artış / gün"
              value={m.realViewGainPerDay != null ? `${fmt(m.realViewGainPerDay, 1)} görüntülenme` : m.realFavGainPerDay != null ? `${fmt(m.realFavGainPerDay, 2)} favori` : "—"}
              sub={m.comparedHours != null ? `son ${fmt(m.comparedHours)} saat` : "ikinci taramadan sonra"}
            />
            <Stat label="Yeni ilan payı (ilk 24)" value={`%${fmt(m.newcomerShare * 100)}`} sub={`medyan yaş ${fmt(m.medianAgeDays)} gün`} />
            <Stat label="Fiyat (USD)" value={`${usd(m.price.p25)} – ${usd(m.price.p75)}`} sub={`medyan ${usd(m.price.median)}`} />
            <Stat label="Kişiselleştirilebilir" value={`%${fmt(m.personalizablePct)}`} sub={m.digitalPct ? `dijital %${fmt(m.digitalPct)}` : undefined} />
            <div>
              <div className="text-xs text-gray-500">Fırsat geçmişi</div>
              {(historyQ.data?.history.length ?? 0) >= 2 ? <Sparkline points={historyQ.data!.history} /> : <div className="text-xs text-gray-400">Takipte birikir</div>}
            </div>
          </div>

          {/* Uzun kuyruk */}
          {expand.error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{(expand.error as Error).message}</div>}
          {longTail && longTail.seed === active.keyword.replace(/^etsy\s+/, "") && (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-700">Uzun kuyruk adayları</h3>
                <span className="text-xs text-gray-400">{longTail.candidates.length} aday · Google'daki Etsy aramaları + rakip etiketleri</span>
                <button onClick={() => batch.mutate([...picked])} disabled={!picked.size || batch.isPending}
                  className="ml-auto inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                  {batch.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Seçilenleri puanla ({picked.size}/{MAX_BATCH})
                </button>
              </div>
              {batch.isPending && <div className="px-4 pt-3 text-xs text-gray-500">Puanlanıyor… kelime başına birkaç saniye sürer.</div>}
              {batch.error && <div className="mx-4 mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{(batch.error as Error).message}</div>}
              {longTail.candidates.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-500">Aday bulunamadı.</div>
              ) : (
                <div className="max-h-[420px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <th className="w-8 px-3 py-2" />
                        <th className="px-3 py-2 font-medium">Kelime</th>
                        <th className="px-3 py-2 font-medium">Kaynak</th>
                        <th className="px-3 py-2 text-right font-medium">Fırsat</th>
                        <th className="px-3 py-2 text-right font-medium">Talep</th>
                        <th className="px-3 py-2 text-right font-medium">Rekabet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...longTail.candidates].sort((a, b) => (b.opportunityScore ?? -1) - (a.opportunityScore ?? -1)).map((c) => (
                        <tr key={c.keyword} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <button onClick={() => togglePick(c.keyword)} aria-label={picked.has(c.keyword) ? "Seçimi kaldır" : "Seç"} className="text-gray-400 hover:text-indigo-600">
                              {picked.has(c.keyword) ? <CheckSquare className="h-4 w-4 text-indigo-600" /> : <Square className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            <button onClick={() => run(c.keyword)} className="text-left text-gray-900 hover:text-indigo-700">{c.keyword}</button>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1">
                              {c.sources.includes("google") && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">arama</span>}
                              {c.sources.includes("tag") && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700" title={`${c.tagCount} rakip ilanda etiket`}>etiket {c.tagCount}</span>}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {c.opportunityScore != null
                              ? <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${scoreTone(c.opportunityScore)}`}>{fmt(c.opportunityScore)}</span>
                              : <span className="text-xs text-gray-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right">{fmt(c.demandScore)}</td>
                          <td className="px-3 py-2 text-right">{fmt(c.competitionScore)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Rakip etiketleri */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700">Rakip etiketleri</h3>
            <p className="mb-2 text-xs text-gray-500">İlk {m.sampled} ilanda en sık geçen etiketler. Tıklayınca o etiket analiz edilir.</p>
            <div className="flex flex-wrap gap-1.5">
              {m.topTags.map((t) => (
                <button key={t.tag} onClick={() => run(t.tag)} title={`${t.count} ilanda · ort. ${t.avgFavPerDay} favori/gün`}
                  className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100">
                  {t.tag} <span className="text-indigo-400">{t.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Ilanlar */}
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-medium">#</th>
                  <th className="px-3 py-2 font-medium">İlan</th>
                  <th className="px-3 py-2 text-right font-medium">Fiyat</th>
                  <th className="px-3 py-2 text-right font-medium">Favori</th>
                  <th className="px-3 py-2 text-right font-medium">Favori/gün</th>
                  <th className="px-3 py-2 text-right font-medium">Görüntülenme/gün</th>
                  <th className="px-3 py-2 text-right font-medium">Artış/gün</th>
                  <th className="px-3 py-2 text-right font-medium">Yaş (gün)</th>
                </tr>
              </thead>
              <tbody>
                {active.listings.map((l) => (
                  <tr key={l.listingId} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs text-gray-400">{l.rank}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {l.imageUrl ? <img src={l.imageUrl} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" loading="lazy" /> : <div className="h-10 w-10 shrink-0 rounded-lg bg-gray-100" />}
                        <a href={l.url} target="_blank" rel="noreferrer" className="line-clamp-2 max-w-md text-gray-700 hover:text-indigo-700" title={l.tags.join(", ")}>
                          {l.title} <ExternalLink className="inline h-3 w-3 text-gray-400" />
                        </a>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">{l.price ? `${l.currency === "USD" ? "$" : ""}${l.price}${l.currency && l.currency !== "USD" ? ` ${l.currency}` : ""}` : "—"}</td>
                    <td className="px-3 py-2 text-right">{fmt(l.favorites)}</td>
                    <td className="px-3 py-2 text-right">{fmt(l.favPerDay, 2)}</td>
                    <td className="px-3 py-2 text-right">{fmt(l.viewsPerDay, 1)}</td>
                    <td className="px-3 py-2 text-right text-xs text-gray-500">
                      {l.viewGainPerDay != null ? `${fmt(l.viewGainPerDay, 1)} gör.` : l.favGainPerDay != null ? `${fmt(l.favGainPerDay, 2)} fav.` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500">{fmt(l.ageDays)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Analiz edilmis kelimeler */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-700">Analiz edilen kelimeler</h3>
          <span className="text-xs text-gray-400">{rows.filter((r) => r.isTracked).length} takipte</span>
        </div>
        {listQ.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-400"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /> Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">Henüz analiz yok. Yukarıdan bir anahtar kelime girin.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Kelime</th>
                  <th className="px-3 py-2 text-right font-medium">Fırsat</th>
                  <th className="px-3 py-2 text-right font-medium">Talep</th>
                  <th className="px-3 py-2 text-right font-medium">Rekabet</th>
                  <th className="px-3 py-2 text-right font-medium">İlan</th>
                  <th className="px-3 py-2 text-right font-medium">Medyan fiyat</th>
                  <th className="px-3 py-2 font-medium">Son tarama</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-t border-gray-100 hover:bg-gray-50 ${active?.keywordId === r.id ? "bg-indigo-50/50" : ""}`}>
                    <td className="px-3 py-2">
                      <button onClick={() => run(r.keyword)} className="font-medium text-gray-900 hover:text-indigo-700">{r.keyword}</button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="inline-flex items-center gap-1">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${scoreTone(r.opportunityScore ?? 0)}`}>{fmt(r.opportunityScore)}</span>
                        {r.opportunityChange != null && r.opportunityChange !== 0 && (
                          r.opportunityChange > 0
                            ? <span className="inline-flex items-center text-xs text-emerald-700"><TrendingUp className="h-3 w-3" />{fmt(r.opportunityChange)}</span>
                            : <span className="inline-flex items-center text-xs text-red-600"><TrendingDown className="h-3 w-3" />{fmt(Math.abs(r.opportunityChange))}</span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{fmt(r.demandScore)}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.competitionScore)}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{fmt(r.totalResults)}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{usd(r.medianPrice)}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{new Date(r.lastScannedAt).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => track.mutate({ id: r.id, tracked: !r.isTracked })}
                          aria-label={r.isTracked ? "Takibi bırak" : "Takibe al"} title={r.isTracked ? "Günlük takipte — bırakmak için tıklayın" : "Günlük takibe al"}
                          className={`rounded-lg p-1.5 ${r.isTracked ? "text-indigo-600 hover:bg-indigo-50" : "text-gray-400 hover:bg-gray-100"}`}>
                          {r.isTracked ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                        </button>
                        <button onClick={() => { if (confirm(`"${r.keyword}" ve geçmişi silinsin mi?`)) remove.mutate(r.id); }}
                          aria-label="Sil" title="Sil" className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
